import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { AuthTopBar } from '../features/auth/ui/auth-top-bar';
import {
  useEmployeeSession,
  useResolvedAuthenticatedPath,
} from '../shared/hooks/use-employee-session';
import {
  BaseButton,
  BaseInput,
  CodeEntrySlots,
  mainHeadingTypographyStyles,
  ViewportFrame,
  ViewportPage,
} from '../shared/ui';

const PIN_LENGTH = 4;

function sanitizePin(value: string): string {
  return value.replace(/\D/g, '').slice(0, PIN_LENGTH);
}

function vibrateOnError() {
  if (typeof navigator.vibrate === 'function') {
    navigator.vibrate([18, 50, 18]);
  }
}

const unlockFieldErrorShake = keyframes`
  0%,
  100% {
    transform: translateX(0);
  }

  20% {
    transform: translateX(-6px);
  }

  40% {
    transform: translateX(6px);
  }

  60% {
    transform: translateX(-4px);
  }

  80% {
    transform: translateX(4px);
  }
`;

const UnlockContent = styled.section.attrs({
  'data-ui': 'session-unlock-page',
})`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const UnlockHeader = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(48)};

  & > [data-ui='auth-top-bar'] {
    margin: 0;
  }
`;

const Intro = styled.div.attrs({
  'data-ui': 'session-unlock-intro',
})`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(7)};
  margin-bottom: ${({ theme }) => theme.scale.px(31)};
`;

const Title = styled.h1`
  ${mainHeadingTypographyStyles};
  width: ${({ theme }) => theme.scale.px(260)};
`;

const Subtitle = styled.p`
  margin: 0;
  width: ${({ theme }) => theme.scale.px(247)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EntrySection = styled.div`
  width: 100%;
  display: grid;
  margin-bottom: ${({ theme }) => theme.scale.px(36)};
`;

const FieldFeedback = styled.div`
  width: ${({ theme }) => theme.scale.px(331)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(6)};
  justify-self: center;
`;

const Prompt = styled.p`
  margin: 0;
  margin-bottom: ${({ theme }) => theme.scale.px(16)};
  text-align: left;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: 1.25;
`;

const PinField = styled.div.attrs({
  'data-ui': 'session-unlock-pin-field',
})<{ $hasError: boolean }>`
  position: relative;
  width: ${({ theme }) => theme.scale.px(331)};
  min-height: ${({ theme }) => theme.scale.px(57)};
  align-self: center;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.surface.subtle};
  border: ${({ $hasError, theme }) =>
    $hasError
      ? `${theme.scale.px(2)} solid ${theme.colors.support.danger}`
      : `${theme.scale.px(1)} solid ${theme.colors.border.subtle}`};
  transition: background-color 160ms ease, box-shadow 160ms ease;
  cursor: text;
  animation: ${({ $hasError }) => ($hasError ? unlockFieldErrorShake : 'none')}
    320ms ease;

  &:focus-within {
    background: ${({ theme }) => theme.colors.surface.overlay};
    box-shadow: ${({ $hasError, theme }) =>
      $hasError
        ? 'none'
        : `0 0 0 ${theme.scale.px(2)} ${theme.colors.brand.primary}`};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HiddenInput = styled(BaseInput)`
  position: absolute;
  width: ${({ theme }) => theme.scale.px(1)};
  height: ${({ theme }) => theme.scale.px(1)};
  padding: 0;
  margin: ${({ theme }) => theme.scale.px(-1)};
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const ErrorText = styled.p`
  margin: 0;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.support.danger};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
`;

const Actions = styled.div`
  display: grid;
  justify-items: center;
`;

const ResetSessionButton = styled(BaseButton)`
  justify-self: center;
  font-size: ${({ theme }) => theme.scale.px(13)};
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export function SessionUnlockPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const redirectPath = useResolvedAuthenticatedPath();
  const { clearSession, dismissUnlockError, unlockError, unlockSession } =
    useEmployeeSession();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (unlockError === null) {
      return;
    }

    setPin('');
    inputRef.current?.focus();
    vibrateOnError();
  }, [unlockError]);

  const handleSubmit = useCallback(async () => {
    if (pin.length !== PIN_LENGTH || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      await unlockSession(pin);
      setPin('');
    } catch {
      clearSession();
      navigate('/login', { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  }, [clearSession, isSubmitting, navigate, pin, unlockSession]);

  useEffect(() => {
    if (
      redirectPath !== '/unlock' ||
      unlockError !== null ||
      pin.length !== PIN_LENGTH ||
      isSubmitting
    ) {
      return;
    }

    void handleSubmit();
  }, [handleSubmit, isSubmitting, pin.length, redirectPath, unlockError]);

  if (redirectPath === null) {
    return null;
  }

  if (redirectPath !== '/unlock') {
    return <Navigate replace to={redirectPath} />;
  }

  return (
    <ViewportPage dataUi="session-unlock-viewport-page">
      <ViewportFrame
        dataUi="session-unlock-viewport-frame"
        paddingBottomPx={28}
        paddingLeftPx={31}
        paddingRightPx={30}
        paddingTopPx={25}
      >
        <UnlockContent>
          <UnlockHeader>
            <AuthTopBar />

            <Intro>
              <Title>{t('sessionUnlock.title')}</Title>
              <Subtitle>{t('sessionUnlock.subtitle')}</Subtitle>
            </Intro>
          </UnlockHeader>

          <EntrySection>
            <FieldFeedback>
              <Prompt>{t('myPageSettings.pin.currentLabel')}</Prompt>

              <PinField
                $hasError={unlockError !== null}
                onClick={() => {
                  inputRef.current?.focus();
                }}
              >
                <HiddenInput
                  ref={inputRef}
                  aria-label={t('myPageSettings.pin.currentLabel')}
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={isSubmitting}
                  inputMode="numeric"
                  value={pin}
                  onChange={event => {
                    if (unlockError !== null) {
                      dismissUnlockError();
                    }

                    setPin(sanitizePin(event.target.value));
                  }}
                />

                <CodeEntrySlots
                  code={pin}
                  codeLength={PIN_LENGTH}
                  digitFontSizePx={20}
                />
              </PinField>

              {unlockError !== null ? (
                <ErrorText>{t('sessionUnlock.error')}</ErrorText>
              ) : null}
            </FieldFeedback>
          </EntrySection>

          <Actions>
            <ResetSessionButton
              type="button"
              onClick={() => {
                clearSession();
                navigate('/login', { replace: true });
              }}
            >
              {t('sessionUnlock.signInAgain')}
            </ResetSessionButton>
          </Actions>
        </UnlockContent>
      </ViewportFrame>
    </ViewportPage>
  );
}
